/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect } from 'react';
import env from '../../../env';
import styles from './style';

export function SpecimenLocatorView(props) {
    const [filters, setFilters] = useState({ 
        categories: [], 
        sampleTypes: [], 
        storages: [],
        primaryCancers: [],
        primaryOrMetastasis: [],
        sites: []
    });
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSampleType, setSelectedSampleType] = useState('');
    const [selectedStorage, setSelectedStorage] = useState('');
    // Cancer-specific filters
    const [selectedPrimaryCancer, setSelectedPrimaryCancer] = useState('');
    const [selectedPrimaryOrMetastasis, setSelectedPrimaryOrMetastasis] = useState('');
    const [selectedSite, setSelectedSite] = useState('');
    
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [filtersLoading, setFiltersLoading] = useState(true);

    // Check if Cancer is selected
    const isCancerSelected = selectedCategory === 'Cancer';

    // Reset cancer-specific filters when category changes
    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
        if (value !== 'Cancer') {
            setSelectedPrimaryCancer('');
            setSelectedPrimaryOrMetastasis('');
            setSelectedSite('');
        }
    };

    // Load filter options on mount
    useEffect(() => {
        const loadFilters = async () => {
            try {
                const response = await fetch(env.apiBase + '/api/specimenLocator/getFilters');
                const data = await response.json();
                setFilters({
                    categories: data.categories || [],
                    sampleTypes: data.sampleTypes || [],
                    storages: data.storages || [],
                    primaryCancers: data.primaryCancers || [],
                    primaryOrMetastasis: data.primaryOrMetastasis || [],
                    sites: data.sites || []
                });
            } catch (error) {
                console.error('Error loading filters:', error);
            } finally {
                setFiltersLoading(false);
            }
        };
        loadFilters();
    }, []);

    const handleSearch = async () => {
        setLoading(true);
        setSearched(true);
        try {
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category', selectedCategory);
            if (selectedSampleType) params.append('sampleType', selectedSampleType);
            if (selectedStorage) params.append('storage', selectedStorage);
            // Cancer-specific filters
            if (isCancerSelected) {
                if (selectedPrimaryCancer) params.append('primaryCancer', selectedPrimaryCancer);
                if (selectedPrimaryOrMetastasis) params.append('primaryOrMetastasis', selectedPrimaryOrMetastasis);
                if (selectedSite) params.append('site', selectedSite);
            }

            const response = await fetch(env.apiBase + `/api/specimenLocator/search?${params.toString()}`);
            const data = await response.json();
            setResults(data || []);
        } catch (error) {
            console.error('Error searching:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = () => {
        if (results.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = [
            'Category',
            'Primary Cancer',
            'Primary or Metastasis',
            'Site',
            'Sample Type',
            'Storage',
            '#_Samples',
            'Data',
            'Details',
            'Biobank',
            'Website',
            'Associated_Services'
        ];

        const rows = results.map(item => [
            item.fieldCategory || '',
            item.fieldPrimaryCancer || '',
            item.fieldPrimaryOrMetastasis || '',
            item.fieldSite || '',
            item.fieldSampleType || '',
            item.fieldStorage || '',
            item.fieldNoOfSamples || '',
            item.fieldData || '',
            item.body || '',
            item.fieldBiobank || '',
            item.fieldWebsite || '',
            item.fieldAssociatedServices || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `specimen_locator_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const hasFiltersSelected = selectedCategory || selectedSampleType || selectedStorage || 
        (isCancerSelected && (selectedPrimaryCancer || selectedPrimaryOrMetastasis || selectedSite));

    return (
        <div css={styles.container}>
            <div css={styles.filterSection}>
                <h4>Search Specimen Locator</h4>
                
                <div css={styles.filterRow}>
                    <div css={styles.filterGroup}>
                        <label>Category</label>
                        <select 
                            value={selectedCategory} 
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            disabled={filtersLoading}
                        >
                            <option value="">- Any -</option>
                            {filters.categories.map((cat, idx) => (
                                <option key={idx} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div css={styles.filterGroup}>
                        <label>Sample type</label>
                        <select 
                            value={selectedSampleType} 
                            onChange={(e) => setSelectedSampleType(e.target.value)}
                            disabled={filtersLoading}
                        >
                            <option value="">- Any -</option>
                            {filters.sampleTypes.map((type, idx) => (
                                <option key={idx} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div css={styles.filterGroup}>
                        <label>Storage</label>
                        <select 
                            value={selectedStorage} 
                            onChange={(e) => setSelectedStorage(e.target.value)}
                            disabled={filtersLoading}
                        >
                            <option value="">- Any -</option>
                            {filters.storages.map((storage, idx) => (
                                <option key={idx} value={storage}>{storage}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Cancer-specific filters - only shown when Cancer is selected */}
                {isCancerSelected && (
                    <div css={styles.filterRow}>
                        <div css={styles.filterGroup}>
                            <label>Primary Cancer</label>
                            <select 
                                value={selectedPrimaryCancer} 
                                onChange={(e) => setSelectedPrimaryCancer(e.target.value)}
                                disabled={filtersLoading}
                            >
                                <option value="">- Any -</option>
                                {filters.primaryCancers.map((item, idx) => (
                                    <option key={idx} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>

                        <div css={styles.filterGroup}>
                            <label>Primary or Metastasis</label>
                            <select 
                                value={selectedPrimaryOrMetastasis} 
                                onChange={(e) => setSelectedPrimaryOrMetastasis(e.target.value)}
                                disabled={filtersLoading}
                            >
                                <option value="">- Any -</option>
                                {filters.primaryOrMetastasis.map((item, idx) => (
                                    <option key={idx} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>

                        <div css={styles.filterGroup}>
                            <label>Site</label>
                            <select 
                                value={selectedSite} 
                                onChange={(e) => setSelectedSite(e.target.value)}
                                disabled={filtersLoading}
                            >
                                <option value="">- Any -</option>
                                {filters.sites.map((item, idx) => (
                                    <option key={idx} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <p css={styles.hint}>Select any filter and click on Apply to see results</p>

                <div css={styles.buttonGroup}>
                    <button 
                        css={styles.applyButton} 
                        onClick={handleSearch}
                        disabled={loading || !hasFiltersSelected}
                    >
                        {loading ? 'Searching...' : 'Apply'}
                    </button>
                    <button 
                        css={styles.exportButton} 
                        onClick={handleExportCSV}
                        disabled={results.length === 0}
                    >
                        Download CSV
                    </button>
                </div>
            </div>

            {loading && (
                <div css={styles.loading}>Loading...</div>
            )}

            {!loading && searched && results.length === 0 && (
                <div css={styles.noResults}>
                    No results found. Please try different filters.
                </div>
            )}

            {!loading && results.length > 0 && (
                <>
                    {/* Desktop Table View */}
                    <div style={{ overflowX: 'auto' }}>
                        <table css={styles.resultsTable}>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Primary Cancer</th>
                                    <th>Site</th>
                                    <th>Sample Type</th>
                                    <th>Storage</th>
                                    <th>#_Samples</th>
                                    <th>Data</th>
                                    <th>Details</th>
                                    <th>Biobank</th>
                                    <th>Associated Services</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.fieldCategory}</td>
                                        <td>{item.fieldPrimaryCancer}</td>
                                        <td>{item.fieldSite}</td>
                                        <td>{item.fieldSampleType}</td>
                                        <td>{item.fieldStorage}</td>
                                        <td>{item.fieldNoOfSamples}</td>
                                        <td>{item.fieldData}</td>
                                        <td>{item.body}</td>
                                        <td>
                                            {item.fieldWebsite ? (
                                                <a href={item.fieldWebsite} target="_blank" rel="noopener noreferrer" css={styles.link}>
                                                    {item.fieldBiobank || item.biobankName}
                                                </a>
                                            ) : (
                                                item.fieldBiobank || item.biobankName
                                            )}
                                        </td>
                                        <td>{item.fieldAssociatedServices}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div css={styles.mobileResults}>
                        {results.map((item, idx) => (
                            <div key={idx} css={styles.mobileCard}>
                                <div css={styles.mobileCardHeader}>
                                </div>
                                <div css={styles.mobileCardBody}>
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>Category</div>
                                        <div css={styles.mobileCardValue}>{item.fieldCategory || '-'}</div>
                                    </div>
                                    {item.fieldPrimaryCancer && (
                                        <div css={styles.mobileCardRow}>
                                            <div css={styles.mobileCardLabel}>Primary Cancer</div>
                                            <div css={styles.mobileCardValue}>{item.fieldPrimaryCancer}</div>
                                        </div>
                                    )}
                                    {item.fieldSite && (
                                        <div css={styles.mobileCardRow}>
                                            <div css={styles.mobileCardLabel}>Site</div>
                                            <div css={styles.mobileCardValue}>{item.fieldSite}</div>
                                        </div>
                                    )}
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>Sample Type</div>
                                        <div css={styles.mobileCardValue}>{item.fieldSampleType || '-'}</div>
                                    </div>
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>Storage</div>
                                        <div css={styles.mobileCardValue}>{item.fieldStorage || '-'}</div>
                                    </div>
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>#_Samples</div>
                                        <div css={styles.mobileCardValue}>{item.fieldNoOfSamples || '-'}</div>
                                    </div>
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>Data</div>
                                        <div css={styles.mobileCardValue}>{item.fieldData || '-'}</div>
                                    </div>
                                    {item.body && (
                                        <div css={styles.mobileCardRow}>
                                            <div css={styles.mobileCardLabel}>Details</div>
                                            <div css={styles.mobileCardValue}>{item.body}</div>
                                        </div>
                                    )}
                                    <div css={styles.mobileCardRow}>
                                        <div css={styles.mobileCardLabel}>Biobank</div>
                                        <div css={styles.mobileCardValue}>
                                            {item.fieldWebsite ? (
                                                <a href={item.fieldWebsite} target="_blank" rel="noopener noreferrer" css={styles.link}>
                                                    {item.fieldBiobank || item.biobankName}
                                                </a>
                                            ) : (
                                                item.fieldBiobank || item.biobankName || '-'
                                            )}
                                        </div>
                                    </div>
                                    {item.fieldAssociatedServices && (
                                        <div css={styles.mobileCardRow}>
                                            <div css={styles.mobileCardLabel}>Associated Services</div>
                                            <div css={styles.mobileCardValue}>{item.fieldAssociatedServices}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
